'use client';
// Onglet « Fichier » de la fiche document : éditeur OnlyOffice embarqué.
// Les droits (édition / révision / commentaires / lecture) sont décidés par le
// serveur dans la configuration signée — rien n'est décidé ici.
import React, { useEffect, useRef, useState } from 'react';
import { fetchEcriture } from '@/lib/ecrire';
import { refusDepot } from '@/lib/depot';
import { useGed } from '@/lib/store';
import type { GedDocument } from '@/lib/types';
import { I, useConfirmation, inputStyle } from '@/components/ui';
import { SuppressionRaisonnee } from '@/components/SuppressionRaisonnee';
import { revisionEnCours, versionApplicable } from '@/lib/version-en-cours';

import { Bouton } from '@/shared/Bouton';
declare global {
  interface Window {
    DocsAPI?: { DocEditor: new (id: string, config: unknown) => { destroyEditor: () => void } };
  }
}

// api.js du Document Server : chargé une seule fois, partagé entre montages.
let dsScript: Promise<void> | null = null;
function loadDs(dsUrl: string): Promise<void> {
  if (window.DocsAPI) return Promise.resolve();
  if (!dsScript) {
    dsScript = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = `${dsUrl}/web-apps/apps/api/documents/api.js`;
      s.onload = () => resolve();
      s.onerror = () => { dsScript = null; reject(new Error('Document Server injoignable')); };
      document.head.appendChild(s);
    });
  }
  return dsScript;
}

const RIGHTS_LABEL: Record<string, string> = {
  edit: 'Édition — les rédacteurs assignés peuvent travailler à plusieurs sur ce fichier.',
  review: 'Mode révision — vos modifications sont tracées et visibles du rédacteur.',
  comment: 'Lecture + commentaires — le contenu ne peut plus être modifié à cette étape.',
  view: 'Lecture seule.',
};

/** Verrou effectif — MIROIR de `verrouActif()` (backend/controllers/verrou.ts) :
 *  un verrou expiré n'en est plus un, ni celui d'un document sorti de la
 *  rédaction (FO-PRE-002, 20/10/2026 : l'approbatrice lisait « en cours de
 *  modification par » une rédactrice qui avait soumis sans rendre la main), et
 *  l'écran ne doit pas afficher une prise en main que le serveur ignore. */
function verrouActif(doc: GedDocument) {
  const v = doc.verrou;
  if (!v?.par) return null;
  if (doc.statut && doc.statut !== 'brouillon' && doc.statut !== 'enrevision') return null;
  return new Date(v.expire).getTime() < Date.now() ? null : v;
}
const jjmm = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso
    : `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
};

/* Le RÉSUMÉ du fichier par l'assistance (17/08) et l'AIDE À LA TRAME (18/08)
   ont été RETIRÉS le 20/10/2026, décision de la direction. L'onglet Fichier ne
   porte plus d'assistant. */

export function OfficeTab({ doc }: { doc: GedDocument }) {
  const { session, act, showToast, refresh, setDocTab } = useGed();
  const { demander, confirmation } = useConfirmation();
  const [err, setErr] = useState<string | null>(null);
  const [rights, setRights] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** Ce que l'application est en train de faire — l'encodage puis l'envoi
   *  prenaient plusieurs secondes sans le moindre retour à l'écran. */
  const [etape, setEtape] = useState('');
  const [plein, setPlein] = useState(false);
  /** Panneau de retrait déplié + son motif. Il remplace un `window.prompt()`,
   *  que le navigateur intégré refuse (« prompt() is not supported ») : la
   *  promesse partait en erreur non rattrapée, et le bouton « Retirer » ne
   *  faisait alors RIEN — ni message, ni trace. Le même blocage existe dans les
   *  WebView et sous certaines politiques d'entreprise. */
  const [retrait, setRetrait] = useState(false);
  const [motifRetrait, setMotifRetrait] = useState('');
  const editorRef = useRef<{ destroyEditor: () => void } | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const f = doc.fichier;
  // Même règle que le serveur : création/remplacement réservés aux rédacteurs
  // AFFECTÉS au document (ou au RQ) — le serveur re-vérifie de toute façon.
  const affectes = (doc.redacteurs?.length ? doc.redacteurs : [doc.redacteur]).filter(Boolean) as string[];
  const enChantier = ['brouillon', 'enrevision'].includes(doc.statut);
  const canCreate = enChantier
    && (session.roles.includes('rq') || (session.roles.includes('redacteur') && affectes.includes(session.key)));
  /* Une RÉVISION en cours : le fichier de travail est le chantier de la version
     suivante, et la version applicable vit dans son instantané. C'est ce qui
     rend la suppression du chantier acceptable — et ce qui en fait, pour le
     rédacteur, une perte sans retour qu'il faut annoncer.
     ⚠️ Ne pas lire `statut === 'enrevision'` : la règle est « une version
     applicable ET une version candidate » (voir `lib/version-en-cours.ts`). */
  const enRevision = revisionEnCours(doc);
  const numApplicable = String(versionApplicable(doc.versions)?.num || doc.versionCourante || '');

  /* ── Document EXTERNE : un exemplaire se dépose, il ne se rédige pas ──────
     Un référentiel acheté (ISO 15189), une notice fournisseur, un certificat :
     le laboratoire n'en écrit pas une ligne, il en détient une COPIE. Il n'a
     donc jamais de statut « brouillon », et `canCreate` y était donc TOUJOURS
     faux : rien ne permettait de déposer le fichier, quel que soit le rôle.
     La garde qui a du sens ici n'est pas l'état du circuit mais le rôle — le
     RQ tient le fonds externe. Le serveur applique la même règle. */
  const estExterne = doc.classe === 'importe';
  const canDeposer = estExterne ? session.roles.includes('rq') : canCreate;
  const famille = estExterne ? 'documentExterne' : 'bureautique';
  const formatsAcceptes = estExterne ? '.pdf,.docx,.xlsx' : '.docx,.xlsx';
  /** Un PDF n'est pas un fichier de travail : pas d'éditeur, on le consulte. */
  const estPdf = String((f as { ext?: string } | null)?.ext || '').toLowerCase() === 'pdf';

  useEffect(() => {
    // Un PDF n'ouvre pas d'éditeur : charger la configuration OnlyOffice pour
    // lui ferait apparaître « Éditeur indisponible » sur une pièce qui n'en a
    // pas besoin — un faux défaut, sur l'écran le plus consulté du fonds.
    if (!f || estPdf) return;
    let cancelled = false;
    void (async () => {
      // `setErr(null)` était en tête d'effet, donc exécuté de façon synchrone
      // au rendu : React 19 y voit un rendu en cascade. Ici il fait partie de la
      // tentative de chargement, ce qu'il a toujours été sur le fond.
      if (!cancelled) setErr(null);
      try {
        const res = await fetch(`/api/ged/ged/docs/${doc.documentId}/office/config`, { credentials: 'include' });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error?.message || data?.error || 'Configuration refusée');
        await loadDs(data.dsUrl);
        if (cancelled || !window.DocsAPI) return;
        editorRef.current?.destroyEditor();
        editorRef.current = new window.DocsAPI.DocEditor('bio24-office-editor', data.config);
        setRights(data.rights);
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : 'Éditeur indisponible');
      }
    })();
    return () => {
      cancelled = true;
      // Demander l'enregistrement AVANT de détruire l'éditeur : une fois
      // l'iframe supprimée, les frappes en attente ne partent plus. `keepalive`
      // laisse la requête aboutir même si la page se ferme dans la foulée.
      void fetchEcriture(`/api/ged/ged/docs/${doc.documentId}/office/flush`, {
        method: 'POST', credentials: 'include', keepalive: true,
      }).catch(() => { /* le Document Server enregistrera à la déconnexion */ });
      editorRef.current?.destroyEditor();
      editorRef.current = null;
    };
    // La clé du fichier change à chaque sauvegarde serveur ; le statut change les droits.
  }, [doc.documentId, f?.key, f?.ext, doc.statut]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fermeture / rechargement de l'onglet navigateur : l'éditeur n'a pas le
  // temps de négocier sa déconnexion, on force l'enregistrement au départ.
  useEffect(() => {
    if (!f) return;
    const flush = () => {
      void fetchEcriture(`/api/ged/ged/docs/${doc.documentId}/office/flush`, {
        method: 'POST', credentials: 'include', keepalive: true,
      }).catch(() => {});
    };
    window.addEventListener('pagehide', flush);
    return () => window.removeEventListener('pagehide', flush);
  }, [doc.documentId, f]);

  // Plein écran : sortie au clavier (Échap) et gel du défilement de la page
  // derrière l'éditeur, qui gère lui-même son propre défilement.
  useEffect(() => {
    if (!plein) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPlein(false); };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [plein]);

  const verrou = verrouActif(doc);
  const maMain = verrou?.par === session.key;
  const estRQ = session.roles.includes('rq');

  /** Télécharge le fichier de travail. Requête à part de la prise en main :
   *  c'est elle que le serveur journalise. */
  const telecharger = async () => {
    const r = await fetch(`/api/ged/ged/docs/${doc.documentId}/fichier`, { credentials: 'include' });
    if (!r.ok) {
      const d = await r.json().catch(() => null);
      showToast(d?.error?.message || d?.error || 'Téléchargement refusé.', 'erreur');
      return false;
    }
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = f?.name || `${doc.code}.docx`;
    a.click();
    URL.revokeObjectURL(a.href);
    return true;
  };

  // Prendre la main PUIS télécharger : si le verrou est refusé (déjà pris), on
  // ne veut pas d'une copie qu'on croirait autorisé à recharger.
  const prendreLaMain = async () => {
    setBusy(true); setEtape('Prise en main…');
    try {
      if (!(await act(doc.documentId, 'verrou'))) return;
      await telecharger();
      showToast('Vous avez la main : modifiez le fichier, puis rechargez-le ici. Elle se libère au rechargement, ou d’elle-même après 7 jours.');
    } finally { setBusy(false); setEtape(''); }
  };

  const rendreLaMain = async () => {
    /* Formulation : elle DÉCRIT la conséquence, elle n'accuse pas. L'ancienne
       (« sans recharger ») se lisait comme un reproche — « vous n'avez pas
       chargé de nouveau document » — y compris quand un rechargement venait
       d'aboutir et que l'écran, lui, était en retard. */
    if (!(await demander(maMain
      ? 'Rendre la main sans recharger de fichier ? Le fichier en ligne reste celui d’avant votre prise en main : les modifications faites sur votre copie ne seront pas reprises.'
      : `Lever la prise en main de ${verrou?.nom} ? Ses modifications hors plateforme ne seront pas reprises.`,
    { confirmer: maMain ? 'Rendre la main' : 'Lever la prise en main' }))) return;
    setBusy(true);
    try { await act(doc.documentId, 'verrou/liberer'); await refresh(); }
    finally { setBusy(false); }
  };

  /**
   * Retrait du fichier attaché — motivé, journalisé.
   *
   * La question posée nomme ce qui va disparaître ET ce qui reste : le fichier
   * s'efface, l'événement demeure. Sans cette précision, retirer une pièce d'un
   * dossier maîtrisé donne l'impression d'effacer une trace, et on n'ose pas.
   */
  const retirerFichier = async (): Promise<string> => {
    /* Le motif n'est plus exigé (décision de la RQ, 14/08/2026) : un document
       diffusé ou appliqué est de toute façon REFUSÉ par le serveur, si bien que
       cette obligation ne pesait que sur des fichiers que personne n'a encore
       lus — le plus souvent un dépôt qu'on corrige dans la minute. Il reste
       possible, et il part au journal quand il est donné. */
    const motif = motifRetrait.trim();
    setBusy(true);
    try {
      const r = await fetchEcriture(`/api/ged/ged/docs/${doc.documentId}/fichier`, {
        method: 'DELETE', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ motif }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => null);
        return d?.error?.message || 'Retrait refusé.';
      }
      setRetrait(false); setMotifRetrait('');
      await refresh();
      showToast('Fichier retiré — le motif et l’empreinte sont au journal.');
      return '';
    } finally { setBusy(false); }
  };

  const create = async (type: 'docx' | 'xlsx') => {
    setBusy(true);
    setEtape(type === 'docx' ? 'Création du document…' : 'Création du classeur…');
    try { await act(doc.documentId, 'office/create', { type }); }
    finally { setBusy(false); setEtape(''); }
  };

  const importFile = async (file: File) => {
    // Seul refus du module qui partait en sévérité « info » : il s'affichait en
    // confirmation navy et disparaissait en 2,6 s, comme une réussite.
    const attendus = estExterne ? /\.(pdf|docx|xlsx)$/i : /\.(docx|xlsx)$/i;
    if (!attendus.test(file.name)) { showToast(`Formats acceptés : ${formatsAcceptes.replace(/,/g, ', ')}`, 'erreur'); return; }
    // Poids et contenu réel, AVANT l'encodage base64 (SEC-10 b) : le refus nomme
    // le poids du fichier et la limite, au lieu de laisser partir 30 Mo pour
    // récolter « Erreur serveur ». Le serveur revalide, et lui seul distingue un
    // .docx d'un .xlsx renommé.
    const refus = await refusDepot(famille, file, 'Le document');
    if (refus) { showToast(refus, 'erreur'); return; }
    setBusy(true);
    try {
      // L'encodage base64 est synchrone et bloque le fil principal : sans ce
      // libellé, l'import d'un gros fichier ressemblait à un gel de l'écran.
      setEtape(`Lecture de ${file.name}…`);
      const base64 = btoa(new Uint8Array(await file.arrayBuffer()).reduce((s, b) => s + String.fromCharCode(b), ''));
      setEtape('Envoi et rechargement…');
      const ok = await act(doc.documentId, 'office/upload', { name: file.name, base64 });
      if (!ok) return;
      /* Relecture EXPLICITE après le rechargement.
         `act` rafraîchit déjà, mais la RQ a constaté le 14/08/2026 un écran qui
         continuait d'annoncer « vous avez la main » après un rechargement
         pourtant abouti — le serveur avait bien libéré le verrou (journal
         `DocumentRecharge`, colonne à `null`). Je n'ai pas su reproduire le cas ;
         cette seconde lecture ne coûte rien et ferme la question. */
      await refresh();
      /* …et on le DIT. « Est-ce une erreur, ou dois-je rafraîchir ? » est la
         question exacte que l'absence de retour a fait poser : un rechargement
         réussi qui ne s'annonce pas laisse croire qu'il a échoué. */
      showToast('Fichier rechargé — la main est rendue, le document redevient modifiable en ligne.');
    } finally { setBusy(false); setEtape(''); }
  };

  if (!f) {
    return (
      <div style={{ padding: 28, display: 'grid', gap: 14, justifyItems: 'center', textAlign: 'center' }}>
        <p style={{ fontSize: 13.5, color: 'var(--text-muted)', margin: 0, maxWidth: 460 }}>
          {estExterne
            ? 'Aucun exemplaire n’est déposé pour ce document externe.'
            : 'Aucun fichier bureautique n’est attaché à ce document.'}
          {canDeposer && estExterne
            ? ' Déposez ici la copie que le laboratoire détient — le PDF de l’éditeur, tel quel. Elle est conservée sans être modifiable : un document externe se consulte, il ne se révise pas.'
            : ''}
          {canDeposer && !estExterne ? ' Créez un document Word — le contenu déjà rédigé y est repris — un classeur Excel, ou importez un fichier existant.' : ''}
        </p>
        {canDeposer && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
            {/* Créer une source éditable n'a aucun sens pour un document
                externe : on n'écrit pas le référentiel de quelqu'un d'autre. */}
            {!estExterne && (
              <>
                <Bouton variante="primaire" disabled={busy} onClick={() => void create('docx')}>
                  <I n="pen" s={14} />Créer un document Word
                </Bouton>
                <Bouton variante="primaire" disabled={busy} onClick={() => void create('xlsx')}>
                  <I n="pen" s={14} />Créer un classeur Excel
                </Bouton>
              </>
            )}
            <Bouton variante={estExterne ? 'primaire' : 'contour'} disabled={busy} onClick={() => fileInput.current?.click()}>
              <I n="diff" s={14} />{estExterne ? 'Déposer l’exemplaire (PDF)' : 'Importer (.docx / .xlsx)'}
            </Bouton>
            <input ref={fileInput} type="file" accept={formatsAcceptes} hidden
              onChange={(e) => { const file = e.target.files?.[0]; if (file) void importFile(file); e.target.value = ''; }} />
          </div>
        )}
        {etape && (
          <div role="status" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12.5, fontWeight: 600, color: 'var(--color-primary-active)', background: 'var(--cyan-50)', border: '1px solid var(--cyan-200)', borderRadius: 'var(--radius-md)', padding: '8px 14px' }}>
            <span className="ged-skel-bar" style={{ width: 12, height: 12, borderRadius: '50%' }} />{etape}
          </div>
        )}
      </div>
    );
  }

  return (
    // Plein écran : on ne change QUE les styles de ce conteneur. Déplacer ou
    // remonter le nœud `#bio24-office-editor` rechargerait l'iframe OnlyOffice
    // et ferait perdre la session d'édition en cours.
    <div style={plein
      ? { position: 'fixed', inset: 0, zIndex: 900, background: 'var(--surface-page)', display: 'flex', flexDirection: 'column' }
      : undefined}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: '1px solid var(--border-subtle)', flexWrap: 'wrap', flex: 'none' }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--navy-900)' }}>{f.name}</span>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{Math.max(1, Math.round(f.size / 1024))} Ko</span>
        {rights && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>· {RIGHTS_LABEL[rights]}</span>}
        <span style={{ flex: 1 }} />
        {!err && (
          <Bouton onClick={() => setPlein(!plein)} title={plein ? 'Quitter le plein écran (Échap)' : 'Afficher l’éditeur en plein écran'}>
            <I n={plein ? 'x' : 'expand'} s={12} />{plein ? 'Réduire' : 'Plein écran'}
          </Bouton>
        )}
        {canDeposer && (
          <>
            {/* ── UN SEUL BOUTON POUR UN SEUL GESTE (07/10/2026) ─────────────
                « Remplacer » et « Recharger le fichier modifié » (bandeau ambre)
                appellent la MÊME route avec la MÊME charge utile, et ouvraient
                déjà le même sélecteur de fichier. Quand la main est prise, les
                deux s'affichaient : deux boutons pour un geste, dont un seul
                nommait la conséquence qui compte — la main est rendue. On garde
                celui du bandeau, qui le dit, et on efface celui-ci. Hors prise
                en main, « Remplacer » reste le geste direct. */}
            {!verrou && (
              <Bouton disabled={busy} onClick={() => fileInput.current?.click()}>
                <I n="diff" s={12} />Remplacer
              </Bouton>
            )}
            {/* Retirer, et pas seulement remplacer : un exemplaire déposé par
                erreur restait attaché indéfiniment, le seul contournement étant
                d'en importer un autre par-dessus. Le motif est exigé — c'est lui
                qui dira, dans six mois, si c'était une erreur ou une décision. */}
            {/* Le retrait n'est offert QUE tant que le document n'est ni en
                vigueur ni diffusé ni ADOPTÉ — miroir des trois gardes serveur.
                Le proposer puis le refuser après le clic serait pire que de ne
                pas le proposer : on aurait promis un geste qu'on ne tient pas.
                (`adopte` manquait : le serveur le refuse depuis le 14/08, et
                l'écran l'offrait encore sur toute édition externe adoptée.) */}
            {/* EN RÉDACTION ET EN RÉVISION AUSSI (RQ, 10/10/2026). Un document
                en révision n'est pas « en vigueur », mais sa liste de diffusion
                est celle de la version précédente : la condition d'origine le
                privait du bouton, et le seul recours était d'écraser le mauvais
                fichier par un autre. Miroir exact de la garde serveur
                (`office.retirerFichier`) : rédaction ou révision, ou document
                ni en vigueur, ni adopté, ni diffusé. */}
            {(enChantier || (doc.statut !== 'envigueur' && doc.statut !== 'adopte' && !(doc.diffusion || []).length)) && (
              <Bouton variante="danger" taille="sm" disabled={busy || retrait} onClick={() => setRetrait(true)}
                title={enRevision
                  ? `Supprimer le fichier de la version en cours. Aucune copie n’en sera conservée ; la v${numApplicable} reste consultable depuis son exemplaire archivé.`
                  : 'Supprimer le fichier attaché. Le motif part au journal, avec le nom et l’empreinte de ce qui a été supprimé.'}>
                <I n="trash" s={12} />Supprimer le fichier
              </Bouton>
            )}
          </>
        )}
      </div>
      {/* ⚠️ LE SÉLECTEUR DE FICHIER VIT HORS DE `canDeposer`. Il y était enfermé,
          alors que « Recharger le fichier modifié » ne dépend, lui, que de
          `maMain` : dès que le circuit avançait après la prise en main (passage
          en vérification, gel d'applicabilité), le bouton restait à l'écran et le
          clic ne faisait ABSOLUMENT RIEN — `fileInput.current` était `null`, sans
          message ni trace. Un bouton qui ne fait rien se lit comme une panne de
          la plateforme, et c'est le mode d'échec le plus coûteux à diagnostiquer.
          Il suit désormais la réunion de ses deux appelants. */}
      {(canDeposer || maMain) && (
        <input ref={fileInput} type="file" accept={formatsAcceptes} hidden
          onChange={(e) => { const file = e.target.files?.[0]; if (file) void importFile(file); e.target.value = ''; }} />
      )}
      {/* Le panneau de retrait se déplie DANS la page, sous la barre du fichier
          — la boîte du navigateur ne pouvait ni citer le fichier correctement
          ni, dans le navigateur intégré, s'ouvrir du tout. */}
      {retrait && f && (
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
          <SuppressionRaisonnee
            titre={enRevision
              ? `Supprimer « ${f.name} », fichier de la version en cours de ${doc.code} ?`
              : `Retirer « ${f.name} » de ${doc.code} ?`}
            objet={<>{f.name} — {Math.max(1, Math.round(f.size / 1024))} Ko</>}
            nature="retrait"
            consequences={[
              /* ⚠️ L'ALERTE QUE LA RQ A DEMANDÉE LE 10/10/2026, dans ses mots :
                 « aucune copie ne sera sauvegardée de ce document en cours de
                 révision ». Elle passe en PREMIER et en « bloquant » : c'est la
                 seule conséquence irréversible du geste. La rotation technique
                 (`_precedentes`) n'est pas une archive et ne rendra rien ;
                 l'instantané, lui, porte la version applicable — pas le
                 chantier. */
              /* ⚠️ `attention` et NON `bloquant` : dans ce socle, une
                 conséquence « bloquante » RETIRE le bouton d'action (le geste
                 n'existe plus tant que l'obstacle n'est pas levé). Ici la RQ
                 demande à être AVERTIE, pas empêchée — la mettre en bloquant
                 aurait supprimé le bouton qu'elle vient de demander. */
              ...(enRevision ? [{
                cle: 'chantier-perdu',
                gravite: 'attention' as const,
                texte: `Aucune copie du fichier en cours de révision ne sera conservée : tout ce qui a été rédigé pour la version `
                  + `en cours est définitivement perdu. La v${numApplicable}, qui s’applique aujourd’hui, reste consultable `
                  + `depuis son exemplaire archivé — elle n’est pas touchée.`,
              }] : []),
              {
                cle: 'trace',
                gravite: 'attention' as const,
                texte: 'Le fichier est effacé du serveur, mais le journal conserve son nom, son poids et son empreinte : la trace reste, seule la pièce s’en va.',
              },
            ]}
            note={(
              <div style={{ display: 'grid', gap: 6 }}>
                <label htmlFor="retrait-motif" style={{ display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>
                  Motif du retrait (facultatif)
                </label>
                <textarea id="retrait-motif" value={motifRetrait} rows={2} autoFocus
                  onChange={(e) => setMotifRetrait(e.target.value)}
                  placeholder="Ex. exemplaire déposé par erreur sur ce document — la bonne version a été versée sur BIO24-EN-014."
                  style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical' }} />
                <span>
                  {enRevision
                    ? 'Le motif reste facultatif, mais il est ici plus utile qu’ailleurs : il dira, dans six mois, pourquoi le chantier de cette version a été repris de zéro.'
                    : 'Ce document n’a jamais été diffusé : le retrait ne demande aucune justification. Si vous en écrivez une, elle part au journal — utile le jour où il faudra distinguer une erreur de dépôt d’une décision.'}
                </span>
              </div>
            )}
            libelleAction={enRevision ? 'Supprimer le chantier de la version en cours' : 'Retirer le fichier'}
            onConfirmer={retirerFichier}
            onAnnuler={() => { setRetrait(false); setMotifRetrait(''); }} />
        </div>
      )}

      {/* Aller-retour hors plateforme (D1). La prise en main ferme AUSSI
          l'éditeur embarqué : deux portes ouvertes sur le même fichier, c'est
          le rechargement qui écrase le travail de l'autre. */}
      {!plein && (verrou || canCreate) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 16px', borderBottom: '1px solid var(--border-subtle)', background: verrou ? 'var(--amber-50)' : 'var(--gray-25)' }}>
          {verrou ? (
            <>
              <I n="lock" s={14} style={{ color: 'var(--amber-700)' }} />
              <span style={{ fontSize: 12.5, color: 'var(--amber-700)', flex: 1, minWidth: 220, lineHeight: 1.5 }}>
                {maMain
                  ? <>Vous avez la main depuis le <strong>{jjmm(verrou.depuis)}</strong> — modifiez le fichier téléchargé, puis rechargez-le ici. L’édition en ligne est suspendue le temps de l’aller-retour ; la main se libère d’elle-même le {jjmm(verrou.expire)}.</>
                  : <>En cours de modification par <strong>{verrou.nom}</strong> depuis le <strong>{jjmm(verrou.depuis)}</strong> — le fichier n’est pas modifiable ici tant qu’il n’a pas rendu la main.</>}
              </span>
              {maMain && (
                <>
                  <Bouton disabled={busy} onClick={() => void telecharger()}>
                    <I n="download" s={12} />Retélécharger
                  </Bouton>
                  <Bouton variante="primaire" disabled={busy} onClick={() => fileInput.current?.click()}>
                    <I n="check" s={12} />Recharger le fichier modifié
                  </Bouton>
                </>
              )}
              {(maMain || estRQ) && (
                <Bouton disabled={busy} onClick={() => void rendreLaMain()}>
                  {maMain ? 'Rendre la main' : 'Lever la prise en main (RQ)'}
                </Bouton>
              )}
            </>
          ) : (
            <>
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)', flex: 1, minWidth: 220, lineHeight: 1.5 }}>
                Pour travailler dans Word plutôt qu’ici : prenez la main, le fichier se télécharge et personne d’autre ne peut le modifier tant que vous ne l’avez pas rechargé.
              </span>
              <Bouton disabled={busy} onClick={() => void prendreLaMain()}>
                <I n="download" s={12} />Prendre la main pour modifier
              </Bouton>
            </>
          )}
        </div>
      )}

      {/* EXEMPLAIRE PDF : pas d'éditeur bureautique, et ce n'est pas une
          limitation — c'est le sens de la pièce. Un référentiel acheté ne
          s'ouvre pas dans un traitement de texte : il se consulte et se
          télécharge. Monter l'éditeur ici laisserait croire qu'on peut le
          modifier, sur un document dont le laboratoire ne détient pas les
          droits. */}
      {estPdf ? (
        <div style={{ padding: 22, display: 'grid', gap: 14, justifyItems: 'start' }}>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-body)', lineHeight: 1.55, maxWidth: '70ch' }}>
            Exemplaire déposé par le laboratoire, conservé <strong>tel quel</strong>. Il n’est pas
            modifiable ici : un document externe se consulte et se remplace, il ne se révise pas.
            {estExterne ? ' La fiche, elle, reste modifiable — édition, détenteur, lieu de consultation.' : ''}
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {/* ⚠️ « Consulter » menait à la route /fichier — c'est-à-dire au
                TÉLÉCHARGEMENT : le navigateur enregistrait le PDF au lieu de
                l'afficher, et le geste contournait la restriction de licence.
                Il ouvre désormais l'onglet Aperçu, qui sert le document EN LIGNE
                (`Content-Disposition: inline`) et applique la restriction.
                Lire et emporter sont deux actes distincts ; ils ne doivent pas
                partager un bouton. */}
            <Bouton variante="primaire" onClick={() => setDocTab('lecture')}>
              <I n="eye" s={14} />Consulter l’exemplaire
            </Bouton>
            {/* Emporter le fichier ne s'offre que si la licence le permet. */}
            {!doc.telechargementRestreint && (
              <Bouton disabled={busy} onClick={() => void telecharger()}>
                <I n="download" s={14} />Télécharger
              </Bouton>
            )}
          </div>
          {doc.telechargementRestreint && (
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, maxWidth: '70ch' }}>
              Cet exemplaire est sous licence : il se lit en entier dans l’aperçu, il ne se télécharge pas.
              Vous pouvez lever cette restriction depuis « Modifier les métadonnées » — c’est une décision
              du laboratoire, pas un verrou technique.
            </p>
          )}
        </div>
      ) : err ? (
        <div style={{ padding: 24, fontSize: 13, color: 'var(--red-600)' }}>
          Éditeur indisponible : {err}. Vérifiez que le serveur OnlyOffice est démarré (docker compose up -d onlyoffice).
        </div>
      ) : (
        <div style={plein ? { flex: 1, minHeight: 0 } : { height: 'min(78vh, 900px)' }}>
          <div id="bio24-office-editor" style={{ height: '100%' }} />
        </div>
      )}
      {confirmation}
    </div>
  );
}
